import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SheetsTable from './SheetsTable';
import PaymentsTable from './PaymentsTable';
import YouTubeCredentials from './YouTubeCredentials';
import CreatorAssistant from './CreatorAssistant';
import StripePaymentMatcher from './StripePaymentMatcher';
import AddPaymentForm from './AddPaymentForm';
import AddTrackableLinkForm from './AddTrackableLinkForm';
import NavigationSidebar from './NavigationSidebar';
import TrackableLinksView from './TrackableLinksView';
import WebhookManager from './WebhookManager';
import BulkPaymentUpload from './BulkPaymentUpload';
import UpgradePromptModal from './UpgradePromptModal';
import OnboardingQuestionnaire from './OnboardingQuestionnaire';
import OnboardingSteps from './OnboardingSteps';
import PieChart from './PieChart';
import BookingsLineChart from './BookingsLineChart';
import VideoMultiSelect from './VideoMultiSelect';
import { bookingsService } from '../services/bookingsService';
import { paymentsService } from '../services/paymentsService';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { CONFIG } from '../config/environment';
import { SheetsData, PieChartData, VideoMetadata } from '../types';
import type { DashboardProps } from '../types';
import { theme } from '../styles/theme';
import Header from './ui/Header';
import Card from './ui/Card';

const ModernDashboard: React.FC<DashboardProps> = ({ user, onLogout }) => {
  const { subscription, onboardingStatus, refreshOnboardingStatus } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Helper variables for subscription checks
  const linkCount = subscription?.linkCount ?? 0;
  const linkLimit = subscription?.linkLimit ?? 25;
  const isAtLinkLimit = linkLimit !== -1 && linkCount >= linkLimit;

  const [bookingsData, setBookingsData] = useState<SheetsData>([]);
  const [paymentsStructuredData, setPaymentsStructuredData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showCreatorAssistant, setShowCreatorAssistant] = useState(false);
  const [showStripePaymentMatcher, setShowStripePaymentMatcher] = useState(false);
  const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
  const [showAddTrackableLinkForm, setShowAddTrackableLinkForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [showOnboardingQuestionnaire, setShowOnboardingQuestionnaire] = useState(false);
  const [showOnboardingSteps, setShowOnboardingSteps] = useState(false);
  const [isOnboardingFlow, setIsOnboardingFlow] = useState(false);
  const [showOnboardingConfetti, setShowOnboardingConfetti] = useState(false);
  // Lead-magnet carry-through state: pre-fill values handed off from the
  // landing page via signup, plus a flag that's true when we successfully
  // auto-created the user's first link.
  const [leadMagnetPrefill, setLeadMagnetPrefill] = useState<{ smLink: string; destinationUrl: string; offer: string } | null>(null);
  const [step3AlreadyComplete, setStep3AlreadyComplete] = useState(false);
  const [typeformWarning, setTypeformWarning] = useState<string | null>(null);
  const [leadMagnetProcessed, setLeadMagnetProcessed] = useState(false);
  const [currentView, setCurrentView] = useState<'bookings' | 'payments' | 'trackable-links' | 'webhooks'>('bookings');
  const [refreshTrackableLinks, setRefreshTrackableLinks] = useState(0);
  const [videoMetadata, setVideoMetadata] = useState<Record<string, VideoMetadata>>({});
  const [videoMetadataLoading, setVideoMetadataLoading] = useState(false);
  const [showRevenueMetrics, setShowRevenueMetrics] = useState(false);
  const [datePreset, setDatePreset] = useState<'30d' | '60d' | '90d' | 'all' | 'custom'>('30d');
  const [dateIsDefault, setDateIsDefault] = useState<boolean>(true);
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd] = useState<string>('');
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
  const [selectionIsDefault, setSelectionIsDefault] = useState<boolean>(true);
  const videoMetadataRef = useRef<Record<string, VideoMetadata>>({});
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [isTablet, setIsTablet] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1100px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mqMobile = window.matchMedia('(max-width: 768px)');
    const mqTablet = window.matchMedia('(max-width: 1100px)');
    const onMobile = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    const onTablet = (e: MediaQueryListEvent) => setIsTablet(e.matches);
    mqMobile.addEventListener('change', onMobile);
    mqTablet.addEventListener('change', onTablet);
    return () => {
      mqMobile.removeEventListener('change', onMobile);
      mqTablet.removeEventListener('change', onTablet);
    };
  }, []);
  const sidebarOffset = isMobile ? 60 : 250;
  const [showFiltersPopover, setShowFiltersPopover] = useState(false);

  // Keep ref in sync with state
  useEffect(() => {
    videoMetadataRef.current = videoMetadata;
  }, [videoMetadata]);

  // Helper function to extract video ID from URL
  const getVideoId = useCallback((url: string) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }, []);

  // Get user's first name from user object
  const getUserFirstName = useCallback((): string => {
    // Use email prefix as display name
    return user.email.split('@')[0];
  }, [user.email]);

  // Resolve the active filter window in absolute Date terms. `null` on either
  // side means "no constraint" (used by the 'all' preset and by partial custom
  // ranges). For the day presets we pin the end to "now" and the start to N
  // days ago, calendar-aligned to UTC midnight so the line chart's date axis
  // matches.
  const dateBounds = useMemo((): { start: Date | null; end: Date | null } => {
    if (datePreset === 'all') return { start: null, end: null };
    if (datePreset === 'custom') {
      const s = customStart ? new Date(customStart + 'T00:00:00Z') : null;
      const e = customEnd ? new Date(customEnd + 'T23:59:59Z') : null;
      if (s && e && s > e) return { start: null, end: null };
      return { start: s, end: e };
    }
    const days = datePreset === '30d' ? 30 : datePreset === '60d' ? 60 : 90;
    const end = new Date();
    const start = new Date();
    start.setUTCHours(0, 0, 0, 0);
    start.setUTCDate(start.getUTCDate() - (days - 1));
    return { start, end };
  }, [datePreset, customStart, customEnd]);

  // Categorical palette — ordered so the first 6 entries are maximally
  // distinguishable by hue (teal → purple → orange → blue → magenta → yellow)
  // before falling through to the secondary tints. Avoids the prior issue of
  // two near-identical greens (#1DB584 and #10D07A) landing in the top 3.
  const COLORS = useMemo(() => [
    '#1DB584', // teal
    '#8B5CF6', // purple
    '#FF6B6B', // coral red
    '#54A0FF', // bright blue
    '#FF9FF3', // pink
    '#FECA57', // golden yellow
    '#10D07A', // bright green
    '#45B7D1', // sky blue
    '#FF8C42', // orange
    '#96CEB4'  // mint green
  ], []);

  const truncateTitle = useCallback((title: string, maxLength: number = 30) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + '...';
  }, []);

  const resolveTitle = useCallback((videoId: string): string => {
    const candidate = videoMetadata[videoId]?.title;
    const isFallback = (t: string | undefined) =>
      !!t && (t.startsWith(`Video ${videoId}`) || t.startsWith(`Video ID: ${videoId}`));
    if (!candidate || isFallback(candidate)) return `Video ID: ${videoId}`;
    return candidate;
  }, [videoMetadata]);

  // Org-scoped slice of the raw bookings rows. All downstream memos derive from
  // this so org filtering happens exactly once.
  const orgBookingRows = useMemo(() => {
    if (!bookingsData || bookingsData.length === 0) return [] as any[];
    const headers = bookingsData[0];
    const orgIdIndex = headers.findIndex((h: string) => h === 'OrgID' || h === 'Org ID');
    if (orgIdIndex === -1) return [] as any[];
    return bookingsData.slice(1).filter(row => row[orgIdIndex] === user.id);
  }, [bookingsData, user.id]);

  const bookingHeaderIndices = useMemo(() => {
    if (!bookingsData || bookingsData.length === 0) {
      return { smLink: -1, dateTime: -1 };
    }
    const headers = bookingsData[0];
    return {
      smLink: headers.findIndex((h: string) => h === 'SM Link'),
      dateTime: headers.findIndex((h: string) => h === 'Date/Time' || h === 'DateTime')
    };
  }, [bookingsData]);

  // The full universe of videos this org has ever booked. Powers the
  // multi-select autocomplete; intentionally NOT date-filtered.
  const availableVideos = useMemo(() => {
    const counts: Record<string, number> = {};
    const { smLink } = bookingHeaderIndices;
    if (smLink === -1) return [] as { videoId: string; title: string }[];
    orgBookingRows.forEach(row => {
      const videoId = getVideoId(row[smLink]);
      if (videoId) counts[videoId] = (counts[videoId] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .map(([videoId]) => ({ videoId, title: resolveTitle(videoId) }));
  }, [orgBookingRows, bookingHeaderIndices, getVideoId, resolveTitle]);

  // Apply the date window to org rows. This is the input to top-10 selection
  // and to every metric/chart/table downstream.
  const dateFilteredRows = useMemo(() => {
    const { start, end } = dateBounds;
    const { dateTime } = bookingHeaderIndices;
    if (start === null && end === null) return orgBookingRows;
    if (dateTime === -1) return orgBookingRows;
    return orgBookingRows.filter(row => {
      const raw = row[dateTime];
      if (!raw) return false;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return false;
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    });
  }, [orgBookingRows, dateBounds, bookingHeaderIndices]);

  // Top 10 video IDs *within the active range*, used both to seed the default
  // multi-select and to keep pie/line color assignment stable.
  const topTenInRange = useMemo(() => {
    const counts: Record<string, number> = {};
    const { smLink } = bookingHeaderIndices;
    if (smLink === -1) return [] as string[];
    dateFilteredRows.forEach(row => {
      const videoId = getVideoId(row[smLink]);
      if (videoId) counts[videoId] = (counts[videoId] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([videoId]) => videoId);
  }, [dateFilteredRows, bookingHeaderIndices, getVideoId]);

  // While the user has not edited the multi-select, keep it synced to the
  // top-10 of whatever range is currently active. As soon as they add or remove
  // a chip we flip `selectionIsDefault` and stop overwriting their choice.
  useEffect(() => {
    if (!selectionIsDefault) return;
    const next = topTenInRange;
    const sameLength = next.length === selectedVideoIds.length;
    const same = sameLength && next.every((id, i) => id === selectedVideoIds[i]);
    if (!same) setSelectedVideoIds(next);
  }, [topTenInRange, selectionIsDefault, selectedVideoIds]);

  // Final filtered set: rows in the date window AND in a selected video.
  const filteredBookingRows = useMemo(() => {
    const { smLink } = bookingHeaderIndices;
    if (smLink === -1) return [] as any[];
    if (selectedVideoIds.length === 0) return [] as any[];
    const allowed = new Set(selectedVideoIds);
    return dateFilteredRows.filter(row => {
      const videoId = getVideoId(row[smLink]);
      return !!videoId && allowed.has(videoId);
    });
  }, [dateFilteredRows, bookingHeaderIndices, getVideoId, selectedVideoIds]);

  // Stable color per videoId. When the user has hand-picked a small set of
  // videos, base the palette on their selection order so positions 0/1/2 get
  // the most-distinct colors (teal/purple/coral) instead of inheriting
  // whichever palette slot they occupied in the broader top-10. In default
  // (untouched) mode we fall back to topTenInRange order so the palette is
  // stable as the date range shifts.
  const colorByVideoId = useMemo(() => {
    const map: Record<string, string> = {};
    const ordered: string[] = selectionIsDefault ? [...topTenInRange] : [...selectedVideoIds];
    // Make sure any id we'll need to color (e.g. legacy selections, top-10
    // entries used by the pie when in default mode) is represented.
    if (selectionIsDefault) {
      selectedVideoIds.forEach(id => { if (!ordered.includes(id)) ordered.push(id); });
    } else {
      topTenInRange.forEach(id => { if (!ordered.includes(id)) ordered.push(id); });
    }
    ordered.forEach((id, i) => { map[id] = COLORS[i % COLORS.length]; });
    return map;
  }, [topTenInRange, selectedVideoIds, selectionIsDefault, COLORS]);

  // Pie chart: count per selected video within the date window.
  const videoRecurrenceData = useMemo((): PieChartData[] => {
    const { smLink } = bookingHeaderIndices;
    if (smLink === -1) return [];
    const counts: Record<string, number> = {};
    filteredBookingRows.forEach(row => {
      const videoId = getVideoId(row[smLink]);
      if (videoId) counts[videoId] = (counts[videoId] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .map(([videoId, count]) => {
        const fullTitle = resolveTitle(videoId);
        if (videoMetadataLoading && fullTitle.startsWith('Video ID: ')) return null;
        return {
          label: truncateTitle(fullTitle),
          fullLabel: fullTitle,
          value: count,
          color: colorByVideoId[videoId] || COLORS[0]
        } as PieChartData;
      })
      .filter((x): x is PieChartData => x !== null);
  }, [filteredBookingRows, bookingHeaderIndices, getVideoId, resolveTitle, truncateTitle, colorByVideoId, COLORS, videoMetadataLoading]);

  // Revenue pie (payments tab): payments aren't date-filtered yet — keep the
  // existing behavior intact for the payments view.
  const revenueByVideoData = useMemo((): PieChartData[] => {
    if (!paymentsStructuredData || paymentsStructuredData.length === 0) return [];
    const revenue: Record<string, number> = {};
    paymentsStructuredData.forEach((payment: any) => {
      const url = payment.smLink || payment.sm_link;
      const videoId = getVideoId(url);
      const amount = parseFloat(payment.amount?.toString().replace(/[$,]/g, '') || '0');
      if (videoId) revenue[videoId] = (revenue[videoId] || 0) + amount;
    });
    return Object.entries(revenue)
      .filter(([, v]) => v > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([videoId, value], index) => {
        const fullTitle = resolveTitle(videoId);
        if (videoMetadataLoading && fullTitle.startsWith('Video ID: ')) return null;
        return {
          label: truncateTitle(fullTitle),
          fullLabel: fullTitle,
          value: Math.round(value),
          color: COLORS[index % COLORS.length]
        } as PieChartData;
      })
      .filter((x): x is PieChartData => x !== null);
  }, [paymentsStructuredData, getVideoId, resolveTitle, truncateTitle, COLORS, videoMetadataLoading]);

  // Per-day, per-video booking counts for the stacked area chart. The date
  // axis is calendar-aligned to the active range so the chart renders zero
  // days as gaps rather than skipping them.
  const bookingsTimeSeriesData = useMemo(() => {
    const { smLink, dateTime } = bookingHeaderIndices;
    if (smLink === -1 || dateTime === -1 || selectedVideoIds.length === 0) {
      return { dates: [] as string[], series: [] as { videoId: string; title: string; color: string; counts: number[] }[] };
    }

    // Per-video per-day map (only for selected videos within the date window).
    const perVideoPerDay: Record<string, Record<string, number>> = {};
    const observedDays = new Set<string>();
    filteredBookingRows.forEach(row => {
      const videoId = getVideoId(row[smLink]);
      if (!videoId) return;
      const raw = row[dateTime];
      if (!raw) return;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return;
      const dayKey = d.toISOString().slice(0, 10);
      observedDays.add(dayKey);
      if (!perVideoPerDay[videoId]) perVideoPerDay[videoId] = {};
      perVideoPerDay[videoId][dayKey] = (perVideoPerDay[videoId][dayKey] || 0) + 1;
    });

    // Calendar-aligned date axis. Prefer the explicit window (start..end) when
    // we have one; fall back to observed-min..observed-max for 'all' time.
    const { start, end } = dateBounds;
    let axisStart: Date | null = start;
    let axisEnd: Date | null = end;
    if (!axisStart || !axisEnd) {
      if (observedDays.size === 0) return { dates: [], series: [] };
      const sorted = Array.from(observedDays).sort();
      axisStart = axisStart || new Date(sorted[0] + 'T00:00:00Z');
      axisEnd = axisEnd || new Date(sorted[sorted.length - 1] + 'T00:00:00Z');
    }
    const dates: string[] = [];
    const cursor = new Date(Date.UTC(axisStart.getUTCFullYear(), axisStart.getUTCMonth(), axisStart.getUTCDate()));
    const stop = new Date(Date.UTC(axisEnd.getUTCFullYear(), axisEnd.getUTCMonth(), axisEnd.getUTCDate()));
    while (cursor <= stop) {
      dates.push(cursor.toISOString().slice(0, 10));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }

    const series = selectedVideoIds.map(videoId => ({
      videoId,
      title: truncateTitle(resolveTitle(videoId)),
      color: colorByVideoId[videoId] || COLORS[0],
      counts: dates.map(d => perVideoPerDay[videoId]?.[d] || 0)
    }));

    return { dates, series };
  }, [filteredBookingRows, bookingHeaderIndices, getVideoId, selectedVideoIds, dateBounds, resolveTitle, truncateTitle, colorByVideoId, COLORS]);

  // 7-day "what's hot" panel — INTENTIONALLY independent of the global filter
  // bar. It always uses the org's full data and a fixed 7-day window so the
  // headline "most booked video this week" callout reads the same regardless
  // of how the user has scoped the rest of the dashboard.
  const { bookingsLast7Days, mostBookedVideoLast7Days } = useMemo((): {
    bookingsLast7Days: number;
    mostBookedVideoLast7Days: { videoId: string | null; count: number; url: string } | null;
  } => {
    const { smLink, dateTime } = bookingHeaderIndices;
    if (smLink === -1 || dateTime === -1) {
      return { bookingsLast7Days: 0, mostBookedVideoLast7Days: null };
    }
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    let total = 0;
    const counts: Record<string, { count: number; url: string }> = {};
    orgBookingRows.forEach(row => {
      const raw = row[dateTime];
      if (!raw) return;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return;
      if (d < sevenDaysAgo) return;
      total++;
      const url = row[smLink];
      const videoId = getVideoId(url);
      if (videoId && url) {
        if (!counts[videoId]) counts[videoId] = { count: 0, url };
        counts[videoId].count++;
      }
    });
    let top: { videoId: string | null; count: number; url: string } | null = null;
    const sorted = Object.entries(counts).sort(([, a], [, b]) => b.count - a.count);
    if (sorted.length > 0) {
      const [topId, data] = sorted[0];
      top = { videoId: topId, count: data.count, url: data.url };
    }
    return { bookingsLast7Days: total, mostBookedVideoLast7Days: top };
  }, [orgBookingRows, bookingHeaderIndices, getVideoId]);

  const fetchVideoMetadata = async (videoIds: string[]) => {
    if (videoIds.length === 0) return;

    setVideoMetadataLoading(true);
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/youtube/videos/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ videoIds })
      });

      if (response.ok) {
        const data: Record<string, VideoMetadata> = await response.json();

        setVideoMetadata(prev => ({ ...prev, ...data }));
      } else {
        console.error('Video metadata fetch failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching video metadata:', error);
    } finally {
      setVideoMetadataLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load bookings and payments data from PostgreSQL
        const [bookingsStructured, paymentsStructured] = await Promise.all([
          bookingsService.fetchBookingsData(user?.id),
          paymentsService.fetchPaymentsData(user?.id).catch(() => []) // Fallback to empty array if fails
        ]);

        // Convert PostgreSQL structured bookings to Google Sheets array format
        // For org A44366: show all columns, for others: show only SM Link and Date/Time
        // Pre-filter bookings for the current user
        const userBookings = bookingsStructured.filter(booking => booking.orgId === user.id);

        const bookingsArrayFormat: SheetsData = user.id === 'A44366' ? [
          ['OrgID', 'SM Link', 'Date/Time', 'Trackable Link', 'First Name', 'Last Name', 'Email', 'Closed', 'Close Amount'],
          ...userBookings.map(booking => [
            booking.orgId,
            booking.smLink,
            booking.dateTime,
            booking.trackableLink || '',
            booking.firstName,
            booking.lastName,
            booking.email || '',
            booking.closed,
            booking.closeAmount
          ])
        ] : [
          ['OrgID', 'SM Link', 'Date/Time'],
          ...userBookings.map(booking => [
            booking.orgId,
            booking.smLink,
            booking.dateTime
          ])
        ];

        setBookingsData(bookingsArrayFormat);
        setPaymentsStructuredData(paymentsStructured);

        // Collect video IDs from both datasets for fetching titles
        const videoIds = new Set<string>();

        // Get video IDs from bookings
        if (bookingsArrayFormat && bookingsArrayFormat.length > 0) {
          const bookingsHeaders = bookingsArrayFormat[0];
          const bookingsOrgIdIndex = bookingsHeaders.findIndex((h: string) => h === 'OrgID' || h === 'Org ID');
          const bookingsSmLinkIndex = bookingsHeaders.findIndex((h: string) => h === 'SM Link');

          const userBookingRows = bookingsArrayFormat.slice(1).filter((row: any) => row[bookingsOrgIdIndex] === user.id);

          userBookingRows.forEach((row: any) => {
            const url = row[bookingsSmLinkIndex];
            const videoId = getVideoId(url);
            if (videoId) {
              videoIds.add(videoId);
            }
          });
        }

        // Get video IDs from payments (structured data)
        if (paymentsStructured && paymentsStructured.length > 0) {
          paymentsStructured.forEach((payment: any) => {
            const url = payment.smLink || payment.sm_link;
            const videoId = getVideoId(url);
            if (videoId) {
              videoIds.add(videoId);
            }
          });
        }

        // Fetch video metadata for all collected video IDs, then set loading to false
        if (videoIds.size > 0) {
          await fetchVideoMetadata(Array.from(videoIds));
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [user.id, getVideoId]);

  // Lead-magnet auto-create flow. When the user lands on /dashboard with
  // smLink + destinationUrl carried from the marketing landing page (either
  // via URL query params on the free-plan path, or sessionStorage on the
  // pro-plan + Stripe round-trip), validate the destination for a Typeform
  // and either auto-create the trackable link OR fall through to onboarding
  // step 3 with a soft warning. Runs at most once per session.
  useEffect(() => {
    if (leadMagnetProcessed) return;
    if (!user?.id) return;
    // Don't try to auto-create if onboarding is already completed.
    if (onboardingStatus?.onboarding_completed) {
      setLeadMagnetProcessed(true);
      return;
    }

    // Read from URL query params first; fall back to sessionStorage for the
    // Stripe round-trip case.
    let smLink = searchParams.get('smLink') || '';
    let destinationUrl = searchParams.get('destinationUrl') || '';
    let offer = searchParams.get('offer') || '';
    if (!smLink && !destinationUrl) {
      try {
        const stashed = sessionStorage.getItem('pendingLeadMagnet');
        if (stashed) {
          const parsed = JSON.parse(stashed);
          smLink = parsed.smLink || '';
          destinationUrl = parsed.destinationUrl || '';
          offer = parsed.offer || '';
        }
      } catch (_) { /* malformed; treat as absent */ }
    }

    if (!smLink || !destinationUrl) {
      setLeadMagnetProcessed(true);
      return;
    }

    // Strip the params from the URL so a refresh doesn't re-trigger this.
    const cleanup = () => {
      try { sessionStorage.removeItem('pendingLeadMagnet'); } catch (_) { /* noop */ }
      navigate('/dashboard', { replace: true });
    };

    let cancelled = false;
    (async () => {
      try {
        const result = await api.trackableLinks.validateDestination(destinationUrl);
        if (cancelled) return;

        if (result.ok && result.hasTypeform) {
          // Auto-create the trackable link.
          try {
            await api.trackableLinks.add({
              orgId: user.id,
              smLink,
              destinationUrl,
              offer: offer || undefined
            });
            if (!cancelled) {
              setStep3AlreadyComplete(true);
              // Refresh booking data so the new link shows up in the table.
              try {
                const bookings = await bookingsService.fetchBookings(user?.id);
                if (!cancelled) setBookingsData(bookings);
              } catch (_) { /* noop — non-fatal */ }
            }
          } catch (err) {
            // Link creation failed — drop into step 3 pre-filled with a
            // generic warning so the user can retry.
            if (!cancelled) {
              setLeadMagnetPrefill({ smLink, destinationUrl, offer });
              setTypeformWarning('We couldn\'t create your link automatically. Double-check the values below and try again.');
            }
          }
        } else {
          // Couldn't verify a Typeform — pre-fill step 3 with a soft warning.
          if (!cancelled) {
            setLeadMagnetPrefill({ smLink, destinationUrl, offer });
            setTypeformWarning(
              result.hasTypeform === false
                ? 'We couldn\'t find a Typeform on that destination URL. Embed one or link directly to typeform.com/to/<id> before publishing.'
                : 'We couldn\'t verify that destination URL. Double-check it before continuing.'
            );
          }
        }
      } finally {
        if (!cancelled) {
          setLeadMagnetProcessed(true);
          cleanup();
        }
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id, onboardingStatus, leadMagnetProcessed, searchParams, navigate]);

  // Check onboarding status on mount. Waits for the lead-magnet flow above
  // so that step3AlreadyComplete is set before the steps modal opens.
  useEffect(() => {
    if (!leadMagnetProcessed) return;
    if (onboardingStatus) {
      const { onboarding_completed, questionnaire_completed } = onboardingStatus;

      // Show questionnaire if not completed
      if (!questionnaire_completed) {
        setShowOnboardingQuestionnaire(true);
      }
      // Show steps if questionnaire is done but onboarding not complete
      else if (questionnaire_completed && !onboarding_completed) {
        setShowOnboardingSteps(true);
      }
    }
  }, [onboardingStatus, leadMagnetProcessed]);

  const handleMatchAccepted = (bookingId: string, amount: number) => {
    const headers = bookingsData[0];
    const collectedIndex = headers.findIndex((h: string) => h.toLowerCase().includes('collected') || h === 'Collected');

    if (collectedIndex !== -1) {
      const updatedData = [...bookingsData];
      const bookingRowIndex = updatedData.findIndex((row, index) =>
        index > 0 && (row[headers.findIndex((h: string) => h === 'ID')] === bookingId ||
        `booking-${index - 1}` === bookingId)
      );

      if (bookingRowIndex > 0) {
        updatedData[bookingRowIndex][collectedIndex] = amount.toString();
        setBookingsData(updatedData);
      }
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    try {
      await paymentsService.deletePayment(user.id, paymentId);

      // Refresh payment data from PostgreSQL
      const paymentsStructured = await paymentsService.fetchPaymentsData(user?.id).catch(() => []);
      setPaymentsStructuredData(paymentsStructured);
    } catch (error) {
      console.error('Error deleting payment:', error);
      throw error;
    }
  };

  const handleCreateLinkClick = () => {
    if (isAtLinkLimit) {
      setShowUpgradePrompt(true);
    } else {
      setShowAddTrackableLinkForm(true);
    }
  };

  const handleQuestionnaireComplete = async (data: {
    howHeardAboutUs: string;
    platformUseCase: string;
    additionalDetails: string;
  }) => {
    try {
      await api.onboarding.saveQuestionnaire(
        data.howHeardAboutUs,
        data.platformUseCase,
        data.additionalDetails
      );

      // Close questionnaire and show steps
      setShowOnboardingQuestionnaire(false);
      setShowOnboardingSteps(true);

      // Refresh onboarding status
      if (refreshOnboardingStatus) {
        await refreshOnboardingStatus();
      }
    } catch (error) {
      console.error('Error saving questionnaire:', error);
      alert('Failed to save questionnaire. Please try again.');
    }
  };

  const handleOnboardingCreateFirstLink = () => {
    setIsOnboardingFlow(true);
    setShowAddTrackableLinkForm(true);
  };

  const handleOnboardingComplete = async () => {
    try {
      // Show confetti
      setShowOnboardingConfetti(true);

      // Wait for confetti animation (5 seconds)
      setTimeout(async () => {
        setShowOnboardingConfetti(false);
        setShowOnboardingSteps(false);
        setIsOnboardingFlow(false);

        // Complete onboarding in backend
        await api.onboarding.completeOnboarding();

        // Refresh onboarding status
        if (refreshOnboardingStatus) {
          await refreshOnboardingStatus();
        }
      }, 5000);
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  const containerStyles: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background
  };

  const mainStyles: React.CSSProperties = {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: isMobile ? theme.spacing.sm : theme.spacing.lg
  };

  const heroStyles: React.CSSProperties = {
    marginBottom: theme.spacing.lg,
    marginTop: theme.spacing.md
  };

  const heroTitleStyles: React.CSSProperties = {
    fontSize: theme.typography.sizes['2xl'],
    fontWeight: theme.typography.weights.bold,
    color: theme.colors.primary,
    marginBottom: theme.spacing.xs,
    letterSpacing: '-0.02em'
  };

  const heroSubtitleStyles: React.CSSProperties = {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: 0
  };

  const statsGridStyles: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: theme.spacing.sm,
    marginBottom: 0
  };

  const statCardStyles: React.CSSProperties = {
    textAlign: 'center'
  };

  const statValueStyles: React.CSSProperties = {
    fontSize: theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.bold,
    color: theme.colors.primary,
    marginBottom: 2,
    lineHeight: 1.1
  };

  const statLabelStyles: React.CSSProperties = {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  };

  const chartsGridStyles: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: theme.spacing.lg,
    marginBottom: theme.spacing['2xl']
  };

  const loadingStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px'
  };

  const spinnerStyles: React.CSSProperties = {
    width: '48px',
    height: '48px',
    border: `4px solid ${theme.colors.border}`,
    borderTop: `4px solid ${theme.colors.accent}`,
    borderRadius: theme.borderRadius.full,
    animation: 'spin 1s linear infinite',
    marginBottom: theme.spacing.lg
  };

  // Calculate stats
  // Stats cards. The filter bar's scope is intentionally limited to the pie
  // and line graph; the stat cards always reflect the full org dataset so the
  // headline numbers don't shift when the user drills into specific videos.
  const { totalBookings, totalRevenue, conversionRate, avgBookingValue } = useMemo(() => {
    if (currentView === 'payments' && paymentsStructuredData.length > 0) {
      const totalPayments = paymentsStructuredData.length;
      const totalPaymentRevenue = paymentsStructuredData.reduce((sum: number, payment: any) => {
        const amount = parseFloat(payment.amount?.toString().replace(/[$,]/g, '') || '0');
        return sum + amount;
      }, 0);
      return {
        totalBookings: totalPayments,
        totalRevenue: totalPaymentRevenue,
        conversionRate: null as number | null,
        avgBookingValue: totalPayments > 0 ? Math.round(totalPaymentRevenue / totalPayments) : 0
      };
    }

    if (currentView === 'bookings' && bookingsData.length > 1) {
      const headers = bookingsData[0];
      const closeAmountIndex = headers.findIndex((h: string) => h === 'Close Amount');
      const closedIndex = headers.findIndex((h: string) => h === 'Closed');

      const totalBookingsCount = orgBookingRows.length;
      const totalBookingRevenue = orgBookingRows.reduce((sum, row) => {
        const amount = parseFloat(row[closeAmountIndex]?.toString().replace(/[$,]/g, '') || '0');
        return sum + amount;
      }, 0);

      const closedCount = orgBookingRows.filter(row => {
        const v = row[closedIndex];
        if (typeof v === 'boolean') return v === true;
        if (typeof v === 'string') return ['Yes', 'YES', 'yes', 'True', 'TRUE'].includes(v);
        return false;
      }).length;
      const bookingConversionRate = totalBookingsCount > 0 ? Math.round((closedCount / totalBookingsCount) * 100) : 0;

      return {
        totalBookings: totalBookingsCount,
        totalRevenue: totalBookingRevenue,
        conversionRate: bookingConversionRate as number | null,
        avgBookingValue: totalBookingsCount > 0 ? Math.round(totalBookingRevenue / totalBookingsCount) : 0
      };
    }

    return { totalBookings: 0, totalRevenue: 0, conversionRate: null as number | null, avgBookingValue: 0 };
  }, [currentView, bookingsData, paymentsStructuredData, orgBookingRows]);

  // Bookings table rows. Each filter (date range, video selection) only
  // narrows the table once the user has actually touched it. Untouched =
  // pass-through. This way the page lands on the full booking list, but as
  // soon as someone changes the date pill or picks a video the table
  // follows that constraint.
  const filteredBookingsData = useMemo(() => {
    if (!bookingsData || bookingsData.length === 0) return bookingsData;

    const dateFilterActive = !dateIsDefault;
    const videoFilterActive = !selectionIsDefault;

    if (!dateFilterActive && !videoFilterActive) {
      return [bookingsData[0], ...orgBookingRows];
    }
    if (dateFilterActive && videoFilterActive) {
      return [bookingsData[0], ...filteredBookingRows];
    }
    if (dateFilterActive) {
      return [bookingsData[0], ...dateFilteredRows];
    }
    // Video filter only — apply the selected-videos predicate to org rows.
    const { smLink } = bookingHeaderIndices;
    if (smLink === -1 || selectedVideoIds.length === 0) {
      return [bookingsData[0], ...orgBookingRows];
    }
    const allowed = new Set(selectedVideoIds);
    const rows = orgBookingRows.filter(row => {
      const videoId = getVideoId(row[smLink]);
      return !!videoId && allowed.has(videoId);
    });
    return [bookingsData[0], ...rows];
  }, [
    bookingsData,
    orgBookingRows,
    dateFilteredRows,
    filteredBookingRows,
    bookingHeaderIndices,
    getVideoId,
    selectedVideoIds,
    selectionIsDefault,
    dateIsDefault
  ]);

  if (showCreatorAssistant) {
    return (
      <CreatorAssistant 
        onBack={() => setShowCreatorAssistant(false)} 
        userId={user.id}
        onOpenCredentials={() => {
          setShowCreatorAssistant(false);
          setShowCredentials(true);
        }}
      />
    );
  }

  // Handle trackable links view
  if (currentView === 'trackable-links') {
    return (
      <div style={containerStyles}>
        <Header
          user={user}
          onLogout={onLogout}
          onOpenYouTubeAPI={() => setShowCredentials(true)}
          onOpenCreatorAssistant={() => setShowCreatorAssistant(true)}
          onOpenStripePayments={() => setShowStripePaymentMatcher(true)}
          onOpenCreateLink={handleCreateLinkClick}
        />

        <NavigationSidebar
          currentView={currentView}
          onNavigate={setCurrentView}
          planTier={subscription?.planTier}
        />

        <div style={{ marginLeft: sidebarOffset }}>
          <TrackableLinksView
            orgId={user.id}
            onBack={() => setCurrentView('bookings')}
            refreshTrigger={refreshTrackableLinks}
            linkCount={linkCount}
            linkLimit={linkLimit}
          />
        </div>

        <AddTrackableLinkForm
          orgId={user.id}
          isOpen={showAddTrackableLinkForm}
          onClose={() => setShowAddTrackableLinkForm(false)}
          onLinkAdded={async () => {
            // Refresh bookings data after adding a new trackable link
            try {
              const bookings = await bookingsService.fetchBookings(user?.id);
              setBookingsData(bookings);

              // Extract video IDs from new bookings and fetch metadata
              const newVideoIds = new Set<string>();
              const currentVideoMetadata = videoMetadataRef.current;
              bookings.forEach((booking: any) => {
                const videoId = getVideoId(booking.sm_link);
                if (videoId && !currentVideoMetadata[videoId]) {
                  newVideoIds.add(videoId);
                }
              });

              if (newVideoIds.size > 0) {
                await fetchVideoMetadata(Array.from(newVideoIds));
              }

              // Refresh the trackable links view
              setRefreshTrackableLinks(prev => prev + 1);
            } catch (error) {
              console.error('Error refreshing bookings:', error);
            }
          }}
        />
      </div>
    );
  }

  // Handle webhooks view
  if (currentView === 'webhooks') {
    return (
      <div style={containerStyles}>
        <Header
          user={user}
          onLogout={onLogout}
          onOpenYouTubeAPI={() => setShowCredentials(true)}
          onOpenCreatorAssistant={() => setShowCreatorAssistant(true)}
          onOpenStripePayments={() => setShowStripePaymentMatcher(true)}
          onOpenCreateLink={handleCreateLinkClick}
        />

        <NavigationSidebar
          currentView={currentView}
          onNavigate={setCurrentView}
          planTier={subscription?.planTier}
        />

        <div style={{ marginLeft: sidebarOffset }}>
          <WebhookManager orgId={user.id} />
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyles}>
      <Header
        user={user}
        onLogout={onLogout}
        onOpenYouTubeAPI={() => setShowCredentials(true)}
        onOpenCreatorAssistant={() => setShowCreatorAssistant(true)}
        onOpenStripePayments={() => setShowStripePaymentMatcher(true)}
        onOpenCreateLink={handleCreateLinkClick}
      />

      <NavigationSidebar
        currentView={currentView}
        onNavigate={setCurrentView}
        planTier={subscription?.planTier}
      />

      <main style={{ ...mainStyles, marginLeft: sidebarOffset }}>
        {/* Hero Section */}
        <div style={heroStyles}>
          <h1 style={heroTitleStyles}>
            Welcome back, {getUserFirstName()}!
          </h1>
          <p style={heroSubtitleStyles}>
            Track your social media performance and optimize your revenue streams.
          </p>
        </div>

        {loading ? (
          <Card>
            <div style={loadingStyles}>
              <div style={spinnerStyles}></div>
              <span style={{ fontSize: theme.typography.sizes.lg, color: theme.colors.text.secondary }}>
                Loading your data...
              </span>
            </div>
          </Card>
        ) : (
          <>
            {/* Stats + charts row. On the bookings view, the 7-day stats live in
                a flush right column so the dashboard reads as one band, no gap
                between the top stats row and the right column. */}
            {(() => {
              const statsBlock = (
                <div style={statsGridStyles}>
                  <Card padding="sm">
                    <div style={statCardStyles}>
                      <div style={statValueStyles}>{totalBookings}</div>
                      <div style={statLabelStyles}>
                        {currentView === 'payments' ? 'Total Payments' : 'Total Bookings'}
                      </div>
                    </div>
                  </Card>

                  {(currentView !== 'bookings' || showRevenueMetrics) && (
                    <Card padding="sm">
                      <div style={statCardStyles}>
                        <div style={statValueStyles}>
                          ${new Intl.NumberFormat().format(totalRevenue)}
                        </div>
                        <div style={statLabelStyles}>Total Revenue</div>
                      </div>
                    </Card>
                  )}

                  {conversionRate !== null && (
                    <Card padding="sm">
                      <div style={statCardStyles}>
                        <div style={statValueStyles}>{conversionRate}%</div>
                        <div style={statLabelStyles}>Conversion Rate</div>
                      </div>
                    </Card>
                  )}

                  {(currentView !== 'bookings' || showRevenueMetrics) && (
                    <Card padding="sm">
                      <div style={statCardStyles}>
                        <div style={statValueStyles}>
                          ${avgBookingValue}
                        </div>
                        <div style={statLabelStyles}>
                          {currentView === 'payments' ? 'Avg. Payment Value' : 'Avg. Booking Value'}
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              );

              if (currentView !== 'bookings') {
                return <>{statsBlock}</>;
              }

              return null;
            })()}

            {/* Charts - View Specific */}
            {videoMetadataLoading && (
              <Card>
                <div style={loadingStyles}>
                  <div style={spinnerStyles}></div>
                  <span style={{ fontSize: theme.typography.sizes.lg, color: theme.colors.text.secondary }}>
                    Loading video titles for charts...
                  </span>
                </div>
              </Card>
            )}

            {currentView === 'bookings' && availableVideos.length > 0 && !videoMetadataLoading && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isTablet ? '1fr' : 'minmax(0, 1fr) 320px',
                gap: theme.spacing.md,
                marginBottom: theme.spacing.lg,
                alignItems: 'start'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md, minWidth: 0 }}>
                  {/* Top stats inside the left column so they share a vertical
                      band with the right-column 7-day stats */}
                  <div style={statsGridStyles}>
                    <Card padding="sm">
                      <div style={statCardStyles}>
                        <div style={statValueStyles}>{totalBookings}</div>
                        <div style={statLabelStyles}>Total Bookings</div>
                      </div>
                    </Card>
                    {showRevenueMetrics && (
                      <Card padding="sm">
                        <div style={statCardStyles}>
                          <div style={statValueStyles}>
                            ${new Intl.NumberFormat().format(totalRevenue)}
                          </div>
                          <div style={statLabelStyles}>Total Revenue</div>
                        </div>
                      </Card>
                    )}
                    {conversionRate !== null && (
                      <Card padding="sm">
                        <div style={statCardStyles}>
                          <div style={statValueStyles}>{conversionRate}%</div>
                          <div style={statLabelStyles}>Conversion Rate</div>
                        </div>
                      </Card>
                    )}
                    {showRevenueMetrics && (
                      <Card padding="sm">
                        <div style={statCardStyles}>
                          <div style={statValueStyles}>${avgBookingValue}</div>
                          <div style={statLabelStyles}>Avg. Booking Value</div>
                        </div>
                      </Card>
                    )}
                  </div>

                  {/* Filter controls. On desktop they render inline above
                      the donut+chart row; on tablet/mobile they collapse
                      behind a Filters button that toggles a popover. */}
                  {(() => {
                    const datePresetLabel = ({
                      '30d': '30D', '60d': '60D', '90d': '90D',
                      all: 'All', custom: 'Custom'
                    } as const)[datePreset];
                    const filterSummary = `Filters · ${datePresetLabel}${
                      !selectionIsDefault && selectedVideoIds.length > 0
                        ? ` · ${selectedVideoIds.length} video${selectedVideoIds.length === 1 ? '' : 's'}`
                        : ''
                    }`;

                    const filterControls = (
                      <>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          flexWrap: isTablet ? 'wrap' : 'nowrap'
                        }}>
                          <div style={{
                            display: 'inline-flex',
                            backgroundColor: '#F3F4F6',
                            borderRadius: 999,
                            padding: 3,
                            gap: 2,
                            flexShrink: 0
                          }}>
                            {([
                              { label: '30D', value: '30d' as const },
                              { label: '60D', value: '60d' as const },
                              { label: '90D', value: '90d' as const },
                              { label: 'All', value: 'all' as const },
                              { label: 'Custom', value: 'custom' as const }
                            ]).map(opt => {
                              const active = datePreset === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  onClick={() => {
                                    setDatePreset(opt.value);
                                    setDateIsDefault(false);
                                  }}
                                  style={{
                                    padding: '5px 10px',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    borderRadius: 999,
                                    border: 'none',
                                    cursor: 'pointer',
                                    backgroundColor: active ? '#FFFFFF' : 'transparent',
                                    color: active ? '#111827' : '#6B7280',
                                    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                    transition: 'all 120ms ease'
                                  }}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>

                          {datePreset === 'custom' && (
                            <>
                              <input
                                type="date"
                                value={customStart}
                                max={customEnd || undefined}
                                onChange={(e) => { setCustomStart(e.target.value); setDateIsDefault(false); }}
                                style={{
                                  padding: '5px 8px',
                                  fontSize: 11,
                                  border: '1px solid #D1D5DB',
                                  borderRadius: 8,
                                  color: '#111827',
                                  backgroundColor: '#FFFFFF',
                                  flexShrink: 0
                                }}
                              />
                              <span style={{ color: '#6B7280', fontSize: 11, flexShrink: 0 }}>to</span>
                              <input
                                type="date"
                                value={customEnd}
                                min={customStart || undefined}
                                onChange={(e) => { setCustomEnd(e.target.value); setDateIsDefault(false); }}
                                style={{
                                  padding: '5px 8px',
                                  fontSize: 11,
                                  border: '1px solid #D1D5DB',
                                  borderRadius: 8,
                                  color: '#111827',
                                  backgroundColor: '#FFFFFF',
                                  flexShrink: 0
                                }}
                              />
                            </>
                          )}

                          <div style={{ flex: 1, minWidth: isTablet ? '100%' : 0 }}>
                            <VideoMultiSelect
                              availableVideos={availableVideos}
                              selected={selectedVideoIds}
                              onChange={(next) => {
                                if (next.length === 0) {
                                  setSelectionIsDefault(true);
                                  return;
                                }
                                setSelectedVideoIds(next);
                                setSelectionIsDefault(false);
                              }}
                              compact
                              hideSelectedChips={selectionIsDefault}
                              placeholder="Search videos"
                            />
                          </div>
                        </div>
                        {datePreset === 'custom' && customStart && customEnd && customStart > customEnd && (
                          <div style={{ marginTop: 6, fontSize: 11, color: '#B91C1C' }}>
                            End must be on or after start.
                          </div>
                        )}
                      </>
                    );

                    if (!isTablet) {
                      return <Card padding="sm">{filterControls}</Card>;
                    }

                    return (
                      <div style={{ position: 'relative' }}>
                        <button
                          onClick={() => setShowFiltersPopover(v => !v)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 14px',
                            backgroundColor: '#FFFFFF',
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: 999,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 600,
                            color: theme.colors.text.primary,
                            boxShadow: theme.shadows.sm
                          }}
                        >
                          <span aria-hidden>⚙︎</span>
                          {filterSummary}
                          <span style={{ color: '#9CA3AF' }}>{showFiltersPopover ? '▲' : '▼'}</span>
                        </button>
                        {showFiltersPopover && (
                          <>
                            <div
                              onClick={() => setShowFiltersPopover(false)}
                              style={{
                                position: 'fixed',
                                inset: 0,
                                zIndex: 50
                              }}
                            />
                            <div style={{
                              position: 'absolute',
                              top: 'calc(100% + 8px)',
                              left: 0,
                              right: 0,
                              zIndex: 51
                            }}>
                              <Card padding="md">{filterControls}</Card>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {videoRecurrenceData.length === 0 ? (
                    <Card padding="md">
                      <div style={{ textAlign: 'center', padding: '32px 16px', color: '#6B7280', fontSize: 13 }}>
                        No bookings match the current filters. Adjust the date range or remove a video to see metrics.
                      </div>
                    </Card>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isTablet ? '1fr' : '260px minmax(0, 1fr)',
                      gap: theme.spacing.md,
                      alignItems: 'stretch'
                    }}>
                      <Card padding="sm">
                        <PieChart
                          data={videoRecurrenceData}
                          title="Top 10 Most Booked Videos"
                        />
                      </Card>

                      {bookingsTimeSeriesData.dates.length > 0 && bookingsTimeSeriesData.series.length > 0 ? (
                        <Card padding="sm">
                          <BookingsLineChart
                            dates={bookingsTimeSeriesData.dates}
                            series={bookingsTimeSeriesData.series}
                            title={
                              selectedVideoIds.length === 1
                                ? 'Bookings Over Time'
                                : `Bookings Over Time — ${selectedVideoIds.length} Videos`
                            }
                          />
                        </Card>
                      ) : <div />}
                    </div>
                  )}

                  {/* Trackable Bookings table — lives inside the left column
                      so the right-column 7-day cards align with this region
                      instead of leaving teal space below the charts. */}
                  <Card>
                    <div style={{
                      marginBottom: theme.spacing.md,
                      paddingBottom: theme.spacing.md,
                      borderBottom: `1px solid ${theme.colors.border}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: theme.spacing.sm
                    }}>
                      <h2 style={{
                        fontSize: theme.typography.sizes.xl,
                        fontWeight: theme.typography.weights.semibold,
                        color: theme.colors.primary,
                        margin: 0
                      }}>
                        Trackable Bookings
                      </h2>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setShowRevenueMetrics(prev => !prev)}
                          style={{
                            backgroundColor: showRevenueMetrics ? '#E5E7EB' : '#FFFFFF',
                            color: '#374151',
                            border: '1px solid #D1D5DB',
                            borderRadius: '26px',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 150ms ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            minHeight: '36px'
                          }}
                        >
                          <span style={{ fontSize: '16px' }}>{showRevenueMetrics ? '👁️' : '🚫'}</span>
                          {showRevenueMetrics ? 'Hide Revenue' : 'Show Revenue'}
                        </button>
                        <button
                          onClick={handleCreateLinkClick}
                          style={{
                            backgroundColor: '#2563eb',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '26px',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 150ms ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            minHeight: '36px'
                          }}
                        >
                          <span style={{ fontSize: '16px' }}>🔗</span>
                          Create Link
                        </button>
                      </div>
                    </div>
                    <SheetsTable data={filteredBookingsData} userId={user.id} showAIAnalysis={true} videoMetadata={videoMetadata} />
                  </Card>
                </div>

                {/* 7-Day Booking Stats */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                  {/* 7-Day Booking Count */}
                  <Card padding="lg">
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        fontSize: theme.typography.sizes['3xl'],
                        fontWeight: theme.typography.weights.bold,
                        color: theme.colors.primary,
                        marginBottom: theme.spacing.xs
                      }}>
                        {bookingsLast7Days}
                      </div>
                      <div style={{
                        fontSize: theme.typography.sizes.sm,
                        color: theme.colors.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Bookings Last 7 Days
                      </div>
                    </div>
                  </Card>

                  {/* Most Booked Video Last 7 Days */}
                  {mostBookedVideoLast7Days && (
                    <Card padding="lg">
                      <div style={{
                        fontSize: theme.typography.sizes.base,
                        fontWeight: theme.typography.weights.semibold,
                        color: theme.colors.primary,
                        marginBottom: theme.spacing.md,
                        textAlign: 'center'
                      }}>
                        Most Booked Video (7 Days)
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                        {/* Video Thumbnail */}
                        <div style={{ textAlign: 'center' }}>
                          <a
                            href={mostBookedVideoLast7Days.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'inline-block' }}
                          >
                            <img
                              src={`https://img.youtube.com/vi/${mostBookedVideoLast7Days.videoId}/mqdefault.jpg`}
                              alt="Video thumbnail"
                              style={{
                                width: '100%',
                                maxWidth: '240px',
                                height: 'auto',
                                borderRadius: theme.borderRadius.md,
                                border: `2px solid ${theme.colors.border}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.02)';
                                e.currentTarget.style.borderColor = theme.colors.accent;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.borderColor = theme.colors.border;
                              }}
                            />
                          </a>
                        </div>

                        {/* Video Stats */}
                        <div style={{ textAlign: 'center' }}>
                          <div style={{
                            fontSize: theme.typography.sizes.lg,
                            fontWeight: theme.typography.weights.bold,
                            color: theme.colors.primary,
                            marginBottom: theme.spacing.xs
                          }}>
                            {mostBookedVideoLast7Days.count} bookings
                          </div>
                          <div style={{
                            fontSize: theme.typography.sizes.sm,
                            color: theme.colors.text.secondary,
                            marginBottom: theme.spacing.sm
                          }}>
                            {mostBookedVideoLast7Days.videoId ?
                            videoMetadata[mostBookedVideoLast7Days.videoId]?.title ||
                            `Video ${mostBookedVideoLast7Days.videoId.slice(0, 8)}...` :
                            'Unknown video'
                          }
                          </div>

                          {/* Video Metrics */}
                          {mostBookedVideoLast7Days.videoId && videoMetadata[mostBookedVideoLast7Days.videoId] && (
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-around',
                              padding: theme.spacing.sm,
                              backgroundColor: theme.colors.background,
                              borderRadius: theme.borderRadius.md,
                              border: `1px solid ${theme.colors.border}`
                            }}>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{
                                  fontSize: theme.typography.sizes.base,
                                  fontWeight: theme.typography.weights.bold,
                                  color: theme.colors.primary
                                }}>
                                  {videoMetadata[mostBookedVideoLast7Days.videoId].viewCount ?
                                    new Intl.NumberFormat().format(parseInt(videoMetadata[mostBookedVideoLast7Days.videoId].viewCount || '0')) :
                                    'N/A'
                                  }
                                </div>
                                <div style={{
                                  fontSize: theme.typography.sizes.xs,
                                  color: theme.colors.text.secondary
                                }}>
                                  Views
                                </div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{
                                  fontSize: theme.typography.sizes.base,
                                  fontWeight: theme.typography.weights.bold,
                                  color: theme.colors.primary
                                }}>
                                  {videoMetadata[mostBookedVideoLast7Days.videoId].likeCount ?
                                    new Intl.NumberFormat().format(parseInt(videoMetadata[mostBookedVideoLast7Days.videoId].likeCount || '0')) :
                                    'N/A'
                                  }
                                </div>
                                <div style={{
                                  fontSize: theme.typography.sizes.xs,
                                  color: theme.colors.text.secondary
                                }}>
                                  Likes
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Video Link */}
                        <div style={{ textAlign: 'center' }}>
                          <a
                            href={mostBookedVideoLast7Days.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: theme.typography.sizes.xs,
                              color: theme.colors.accent,
                              textDecoration: 'none'
                            }}
                          >
                            View on YouTube →
                          </a>
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              </div>
            )}

            {currentView === 'payments' && revenueByVideoData.length > 0 && !videoMetadataLoading && (
              <div style={chartsGridStyles}>
                <Card>
                  <PieChart
                    data={revenueByVideoData}
                    title="Top 10 Videos with Most Revenue"
                    valueType="currency"
                  />
                </Card>
              </div>
            )}


            {/* Data Table — only for non-bookings views (bookings table now
                lives inside the outer 2-col grid above). */}
            {currentView !== 'bookings' && <Card>
              <div style={{
                marginBottom: theme.spacing.lg,
                paddingBottom: theme.spacing.lg,
                borderBottom: `1px solid ${theme.colors.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h2 style={{
                  fontSize: theme.typography.sizes['2xl'],
                  fontWeight: theme.typography.weights.semibold,
                  color: theme.colors.primary,
                  margin: 0
                }}>
                  Payments
                </h2>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {currentView === 'payments' && (
                    <button
                      onClick={() => setShowBulkUpload(true)}
                      style={{
                        backgroundColor: '#3B82F6',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '26px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 150ms ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        minHeight: '36px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#2563EB';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#3B82F6';
                        e.currentTarget.style.transform = 'translateY(0px)';
                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>📁</span>
                      Bulk Upload
                    </button>
                  )}
                  {currentView === 'payments' && (
                    <button
                      onClick={() => setShowAddPaymentForm(true)}
                      style={{
                        backgroundColor: '#10D07A',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '26px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 150ms ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        minHeight: '36px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#22C55E';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#10D07A';
                        e.currentTarget.style.transform = 'translateY(0px)';
                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>+</span>
                      Add Payment
                    </button>
                  )}
                </div>
              </div>
              <PaymentsTable
                data={[]}
                userId={user.id}
                structuredData={paymentsStructuredData}
                onDeletePayment={handleDeletePayment}
                videoMetadata={videoMetadata}
              />
            </Card>}
          </>
        )}
      </main>

      {/* Modals */}
      <YouTubeCredentials
        userId={user.id}
        isOpen={showCredentials}
        onClose={() => setShowCredentials(false)}
        onCredentialsUpdated={() => {}}
      />

      {showStripePaymentMatcher && (
        <StripePaymentMatcher
          userId={user.id}
          bookingsData={bookingsData}
          onMatchAccepted={handleMatchAccepted}
          onClose={() => setShowStripePaymentMatcher(false)}
        />
      )}

      <AddTrackableLinkForm
        orgId={user.id}
        isOpen={showAddTrackableLinkForm}
        initialSmLink={leadMagnetPrefill?.smLink}
        initialDestinationUrl={leadMagnetPrefill?.destinationUrl}
        initialOffer={leadMagnetPrefill?.offer}
        destinationWarning={typeformWarning}
        onClose={() => {
          setShowAddTrackableLinkForm(false);
          // If this was during onboarding, make sure steps modal is visible
          if (isOnboardingFlow && !showOnboardingSteps) {
            setShowOnboardingSteps(true);
          }
        }}
        onLinkAdded={async (createdLink) => {
          // Refresh bookings data after adding a new trackable link
          try {
            const bookings = await bookingsService.fetchBookings(user?.id);
            setBookingsData(bookings);

            // Extract video IDs from new bookings and fetch metadata
            const newVideoIds = new Set<string>();
            const currentVideoMetadata = videoMetadataRef.current;
            bookings.forEach((booking: any) => {
              const videoId = getVideoId(booking.sm_link);
              if (videoId && !currentVideoMetadata[videoId]) {
                newVideoIds.add(videoId);
              }
            });

            if (newVideoIds.size > 0) {
              await fetchVideoMetadata(Array.from(newVideoIds));
            }

            // Refresh the trackable links view
            setRefreshTrackableLinks(prev => prev + 1);

            // If this was during onboarding, complete onboarding
            if (isOnboardingFlow && createdLink) {
              await handleOnboardingComplete();
            }
          } catch (error) {
            console.error('Error refreshing bookings:', error);
          }
        }}
      />

      <AddPaymentForm
        userId={user.id}
        isOpen={showAddPaymentForm}
        onClose={() => setShowAddPaymentForm(false)}
        onPaymentAdded={async () => {
          // Refresh payments data after adding a new payment
          try {
            const paymentsStructured = await paymentsService.fetchPaymentsData(user?.id).catch(() => []);
            setPaymentsStructuredData(paymentsStructured);

            // Extract video IDs from new payments and fetch metadata
            const newVideoIds = new Set<string>();
            const currentVideoMetadata = videoMetadataRef.current;
            paymentsStructured.forEach((payment: any) => {
              const videoId = getVideoId(payment.smLink || payment.sm_link);
              if (videoId && !currentVideoMetadata[videoId]) {
                newVideoIds.add(videoId);
              }
            });

            // Fetch metadata for new video IDs
            if (newVideoIds.size > 0) {
              await fetchVideoMetadata(Array.from(newVideoIds));
            }
          } catch (error) {
            console.error('Error refreshing payments data:', error);
          }
        }}
      />

      {showBulkUpload && (
        <BulkPaymentUpload
          orgId={user.id}
          onUploadComplete={async () => {
            // Refresh payments data after bulk upload
            try {
              const paymentsStructured = await paymentsService.fetchPaymentsData(user?.id).catch(() => []);
              setPaymentsStructuredData(paymentsStructured);

              // Extract video IDs from new payments and fetch metadata
              const newVideoIds = new Set<string>();
              const currentVideoMetadata = videoMetadataRef.current;
              paymentsStructured.forEach((payment: any) => {
                const videoId = getVideoId(payment.smLink || payment.sm_link);
                if (videoId && !currentVideoMetadata[videoId]) {
                  newVideoIds.add(videoId);
                }
              });

              // Fetch metadata for new video IDs
              if (newVideoIds.size > 0) {
                await fetchVideoMetadata(Array.from(newVideoIds));
              }
            } catch (error) {
              console.error('Error refreshing payments data:', error);
            }
          }}
          onClose={() => setShowBulkUpload(false)}
        />
      )}

      <UpgradePromptModal
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        linkCount={linkCount}
        linkLimit={linkLimit}
      />

      <OnboardingQuestionnaire
        isOpen={showOnboardingQuestionnaire}
        onComplete={handleQuestionnaireComplete}
      />

      <OnboardingSteps
        isOpen={showOnboardingSteps}
        onComplete={handleOnboardingComplete}
        onCreateFirstLink={handleOnboardingCreateFirstLink}
        showConfetti={showOnboardingConfetti}
        orgId={user.id}
        initialStep3Complete={step3AlreadyComplete}
      />

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ModernDashboard;